import { attributes, AttributesMixin, Collection, define, Events, memberOf, Model, Store, type } from '@type-r/models';
import { MultiSelectFilter, TextFilter } from 'lqx-react';
import { matchString, SortOptions } from './common';
import { fetchJson, restfulIO } from './endpoint';
import { ParticipantRoles, Permissions } from './permissions';
import { Email } from '@type-r/ext-types';

export enum IdentifierType {
    BankAccount = 'BankAccount',
    ExternalRelationshipId = 'ExternalRelationshipId',
    NameAlias = 'NameAlias',
    EmailDomain = 'EmailDomain',
    ISA = 'ISA',
    GSId = 'GSId'
}
export class Identifier extends attributes({
    key: String,
    value: String,
    shared: type(Boolean).dontSave,
}) { }
export interface CompanyModel extends AttributesMixin<typeof CompanyModel> {
}

const ShortString = (error) =>
    type(String)
        .check(x => x == null || x.length <= 9, error);

@define
export class CompanyModel extends Model {
    static endpoint = restfulIO('/api/inblock/tradetalk/v1/Company');

    static attributes = {
        salesforceId: String,
        externalId: type(String).parse(x=>x||0),
        externalIdentifier: type(String).parse(x=>x||''),
        name: type(String).required,
        shortname: String,
        dba: String,
        phone: String,
        addressLine1: type(String).required,
        addressLine2: type(String),
        city: type(String).required,
        stateRegion: type(String).required,
        postalCode: type(String).required,
        country: type(String).required,
        entityType: type(String),
        duns: ShortString('Duns number is too long'),
        ultimateDuns: ShortString('Ultimate Duns number is too long'),
        taxid: ShortString('Tax ID is too long'),
        stateOfIncorporation: String,
        countryOfJurisdiction: String,
        isActive: Boolean,
        createdOn: Date,
        createdBy: String,
        updatedOn: Date,
        updatedBy: String,
        inblockId: type(String).dontSave,
        legalEntityId: String,
        fingerprint: type(String).dontSave,
        lqxId: type(Number).dontSave,

        bankAccount: Array,
        identifiers: [Identifier],
        user: Array
    };

    match(text: string) {
        return !text || text.length < 2
            || matchString(this.name, text);
    }
}

export const companyIdentifierTypes = [IdentifierType.BankAccount,
                                    IdentifierType.NameAlias,
                                    IdentifierType.ISA, IdentifierType.EmailDomain,
                                    IdentifierType.GSId];
export const relationshipIdentifierTypes = [IdentifierType.BankAccount,
                                    IdentifierType.ExternalRelationshipId,
                                    IdentifierType.ISA, IdentifierType.EmailDomain,
                                    IdentifierType.GSId];
export enum ProductModuleNames {
    OrderManagement = 'OrderManagement',
    InvoiceManagement = 'InvoiceManagement',
    Inmatch = 'Inmatch',
    CashflowForecasting = 'CashflowForecasting',
    RelationshipDashboard = 'RelationshipDashboard',
}

const PositiveNumber = type(Number).check(x => x >= 0, 'Invalid range')

export interface RelationshipFilters extends AttributesMixin<typeof RelationshipFilters> { }
@define
export class RelationshipFilters extends Model {
    static attributes = {
        filterAll: '',
        supplierName: MultiSelectFilter,
        customerName: MultiSelectFilter,
        workflow: TextFilter,
        relationshipBankAccount: MultiSelectFilter,
        customerExternalId: TextFilter,
        supplierExternalId: TextFilter
    }

    initializeOptions(relationships: Collection<RelationshipModel>) {
        this.filterAll = '';
        this.customerExternalId.set({ value: undefined });
        this.supplierExternalId.set({ value: undefined });
        this.workflow.set({ value: undefined });
        this.relationshipBankAccount.set({ selected: undefined, options: Array.from(['Test-306061', 'Test-2365741']) });
        const supplierNames = relationships.map(r => r.Supplier && r.Supplier.companyName);
        this.supplierName.set({ selected: undefined, options: Array.from(new Set(supplierNames.filter(x => x))) });
        const customerNames = relationships.map(r => r.Customer && r.Customer.companyName);
        this.customerName.set({ selected: undefined, options: Array.from(new Set(customerNames.filter(x => x))) });
    }
}

export type WorkflowMap = typeof WorkflowMap;
export const WorkflowMap = attributes({
    id: type(Number),
    description: String
});

export interface Participant extends AttributesMixin<typeof Participant> { }
@define
export class Participant extends Model {
    static attributes = {
        role: String,
        isActive: Boolean,
        externalRelationshipId: String,
        supplierExternalId: String,
        companyId: Number,
        companyName: String,
        companyShortname: String,
        companyInblockId: String,
        companyLegalEntityId: String,
        companyLqxId: type(Number).dontSave,
        mep: String,
        subMep: String,
        identifiers: type([Identifier])
    };

    get filterSelectionName(): string {
        return this.companyShortname || this.companyName || '';
    }
}

export interface Supplier extends AttributesMixin<typeof Supplier> { }
@define
export class Supplier extends Model {
    static endpoint = restfulIO('/api/pts/suppliers');

    static attributes = {
        name: String,
        supplierId: Number,
        isActive : Boolean,
        platformCode: String,
        roleName: String,
        productCode : String
    };
}

export interface Conversation extends AttributesMixin<typeof Conversation> { }
@define
export class Conversation extends Model {
    static endpoint = restfulIO('/api/inblock/tradetalk/v1/Conversation');
    static attributes = {
        relationshipId: Number,
        workflowMap: WorkflowMap
    };
}

export interface WorkflowMapModel extends AttributesMixin<typeof WorkflowMapModel> { }
@define
export class WorkflowMapModel extends Model {
    static endpoint = restfulIO('/api/inblock/tradetalk/v1/WorkflowMap');
    static attributes = {
        description: String,
        createdOn : Date,
        createdBy : String,
        updatedOn : Date,
        updatedBy : String,
        conversation : Array,
        workflowStep : Array
    }
}

export const workflowStepHandlerBaseAddress = '/api/inblock/tradetalk/v1/WorkflowStep';
export interface WorkflowStepHandlerDescriptionModel extends AttributesMixin<typeof WorkflowStepHandlerDescriptionModel> { }
@define
export class WorkflowStepHandlerDescriptionModel extends Model {
    static endpoint = restfulIO(workflowStepHandlerBaseAddress);
    static attributes = {
        description: String,
        ehId: Number,
        ehDescription: String,
        metadata: String,
        jsonSchema: String,
        ehSuccessId: Number,
        ehSuccessDescription: String,
        ehFailId: Number,
        ehFailDescription: String,
    }

    static async getByWorkflowId(workflowId: String) {
        const url = `${workflowStepHandlerBaseAddress}/WorkflowStepHandlerDescriptions/${workflowId}`;
        return await fetchJson('GET', url);
    }
}

// relationship notification configuration
export interface EmailConfig extends InstanceType<typeof EmailConfig> { };
export const EmailConfig = attributes({
    isEnabled: Boolean,
    isRealTime: Boolean,
    hours: Number,
    minutes: Number,
    startTime: "10:00",
    emails: Array
});

export interface CustomerNotificationConfig extends InstanceType<typeof CustomerNotificationConfig> { }

export const CustomerNotificationConfig = attributes({
    isEnabled: Boolean,
    invoiceConfig: EmailConfig,
    disputeConfig: EmailConfig,
    disputeResolvedConfig: EmailConfig
});

export interface SupplierNotificationConfig extends InstanceType<typeof SupplierNotificationConfig> { }

export const SupplierNotificationConfig = attributes({
    isEnabled: Boolean,
    approveInvoiceConfig: EmailConfig,
    disputeConfig: EmailConfig,
    disputeResolvedConfig: EmailConfig,
    submitRemittanceAdviceConfig: EmailConfig
});

interface RelationshipNotificationConfig extends AttributesMixin<typeof RelationshipNotificationConfig> { }

const RelationshipNotificationConfig = attributes({
    customerConfig: CustomerNotificationConfig,
    supplierConfig: SupplierNotificationConfig
});


export type RelationshipPermissions = typeof RelationshipPermissions;
export const RelationshipPermissions = attributes({
    relationshipId: Number,
    role: String,
    permissions: Permissions,
    relationshipParticipantUserId: Number
});
export type RelationshipUserInfo = InstanceType<typeof RelationshipUserInfo>
export const RelationshipUserInfo = attributes({
    ...RelationshipPermissions.attributes,
    firstName: String,
    lastName: String,
    userName: String,
    companyId: String,
});



export interface CustomerProfileInvoicingModel extends InstanceType<typeof CustomerProfileInvoicingModel> { };
export const CustomerProfileInvoicingModel = attributes({
    customerId: String,
    sendingMethod: String,
    sendingResponsibility: String,
    invoiceSubmissionOwner: String,
    emails: Array,
    webPortalUrl: String,
    webPortalUser: String,
    webPortalPwd: String,
    frequency: String,
    autoSendInvoices: Boolean,
    sendInvoicesIndividually: Boolean,
    streetAddress: String,
    addressNumber: Number,
    zipCode: String,
    city: String,
    region: String,
    country: String
});

export interface CustomerProfileStatementModel extends InstanceType<typeof CustomerProfileStatementModel> { };
export const CustomerProfileStatementModel = attributes({
    customerId: String,
    sendingMethod: String,
    uacIncludedInSoa: Boolean,
    receiptMailAddress: Array,
    webPortalUrl: String,
    webPortalUser: String,
    webPortalPwd: String,
    frequency: String,
    autoSendSoa: Boolean,
    soaDueIn2Weeks: Boolean
});

export interface CustomerProfilePaymentModel extends InstanceType<typeof CustomerProfilePaymentModel>{};
export const CustomerProfilePaymentModel = attributes ({
    reconciliationOfPayments : true,
    autoBookPayments : Boolean,
    paymentOnBehalf : String,
    remittanceAdviceNotRequired : type(Boolean).value(undefined),
    sendToPTS : Boolean,
    amountSendToPayor : Boolean,
    customerId : String,
    bankName : String,
    bankAccountNumber : Array,
    bankCurrency : String,
    zbaName : String,
    frequencyOfPayment : String,
    frequencyOfPaymentDetail : String,
    methodOfPayment : String,
    paymentReference : String,
    paymentConfirmationType : String,
    paymentConfirmationDetail: Array,
    remittanceAdviceRequired: false,
    disableNegativeAdjustments : false,
    amountSettleOffPlatform: false,
});

const PhoneNumber = type(String)
    .check(
        x => !x || Boolean(x.match(/^\+?([0-9]|\-|\.|\s|\(|\))+$/)),
        "not a phone number."
    )

export interface ExternalContactInfo extends InstanceType<typeof ExternalContactInfo> { };
export const ExternalContactInfo = attributes({
    name : String,
    jobTitle : String,
    phone : PhoneNumber,
    streetAddress : String,
    addressNumber: String,
    city: String,
    zipPostalCode: String,
    stateRegion : String,
    country: String,
    emailAddress : Email,
    escalationPointName : String,
    escalationPointEmail: Email
});

export interface CustomerProfileExternalContactModel extends InstanceType<typeof CustomerProfileExternalContactModel>{};
export const CustomerProfileExternalContactModel = attributes ({
    primaryContact: ExternalContactInfo,
    secondaryContact: ExternalContactInfo,
    tertiaryContact: ExternalContactInfo
});

const ToleranceAmountType = type(Number).value(0).check(x => !x || x >= 0);
const TolerancePercentType = type(Number).value(0).check(x => !x || (x >= 0 && x <= 100));

export interface CustomerProfileToleranceModel extends InstanceType<typeof CustomerProfileToleranceModel>{};
export const CustomerProfileToleranceModel = attributes ({
    toleranceAmount: ToleranceAmountType,
    tolerancePercent: TolerancePercentType,
    reconciliationToleranceAmount: ToleranceAmountType,
    reconciliationTolerancePercent: TolerancePercentType,
});

export interface RelationshipModel extends AttributesMixin<typeof RelationshipModel> { }
@define
export class RelationshipModel extends Model {
    static endpoint = restfulIO('/api/inblock/tradetalk/v2/Relationship');
    static attributes = {
        lqxId: Number,
        paymentTerms: String,
        incoTerms: type(String).check( x => !x || x.length <= 3, 'Should be less or equal than 3 characters' ),
        governingLaw: String,
        currency: String,
        creditLimit: Number,
        customerAutoAcceptThreshold: Number,
        earlyPayDiscountPercentage: Number,
        earlyPayThreshold: Number,
        billToCompanyName: String,
        region: String,
        gbsUnit: String,
        erp: String,
        mainSegment: String,
        olsCustomer: String,
        fdssCustomer: String,
        supplierExternalId: String,
        defaultWorkflowMap: memberOf('store.workflows', WorkflowMapModel).parse(x => x.id).toJSON(x => x),

        conversationIdList: Array,
        participants: [Participant],
        productModules: Array,
        identifiers: type([Identifier]),
        notificationConfig: RelationshipNotificationConfig,
        relationshipUsers: type([RelationshipUserInfo]).dontSave,
        cpInvoicing: type(CustomerProfileInvoicingModel).dontSave.endpoint(restfulIO("./invoicing")),
        cpStatement: type(CustomerProfileStatementModel).dontSave.endpoint(restfulIO("./statement")),
        cpPayment : type(CustomerProfilePaymentModel).dontSave.endpoint(restfulIO("./payment")),
        cpContact: type(CustomerProfileExternalContactModel).dontSave.endpoint(restfulIO("./contact")),
        cpDiscount: type(CustomerProfileToleranceModel).dontSave.endpoint(restfulIO("./discount")),
    };


    get Customer(): Participant {
        return this.participants && this.participants.find(x => x.role === ParticipantRoles.Customer);
    }

    get customer(): CompanyModel {
        return (Store.global as any).companies.get(this.Customer.companyId as any);
    }

    get supplier(): CompanyModel {
        return (Store.global as any).companies.get(this.Supplier.companyId as any);
    }

    get Supplier(): Participant {
        return this.participants && this.participants.find(x => x.role === ParticipantRoles.Supplier);
    }
    static sortBy = {
        supplierRelationshipId: 'Supplier Relationship ID',
        customerRelationshipId: 'Customer Relationship ID',
        supplierName: 'Supplier Name',
        customerName: 'Customer Name',
    }

    static createFilter(filter: string, sort: SortOptions) {
        return x => x.match(filter);
    }
    get title(): string {
        const titleComponents = [
            this.Customer && this.Customer.filterSelectionName,
            this.Supplier && this.Supplier.filterSelectionName].filter(
                x => x
            );
        //   if (wfMap && wfMap.description) {
        //     titleComponents.push(wfMap.description);
        //   }
        return titleComponents.join(" - ");
    }

    filter(filter: RelationshipFilters): boolean {
        return (!!filter.customerExternalId.value ? matchString(this.Customer && this.Customer.externalRelationshipId, filter.customerExternalId.value) : true)
            && filterByMultiSelect(filter.supplierName, this.Supplier && this.Supplier.companyName)
            && filterByMultiSelect(filter.customerName, this.Customer && this.Customer.companyName);
    }

    get ShowSupplier(): boolean {
        if (
            (!this.Supplier && !this.Customer) ||
            (this.Supplier && this.Customer) ||
            (this.Supplier && !this.Customer)
        ) return true;

        return false;
    }

    get ShowCustomer(): boolean {
        if (
            (!this.Supplier && !this.Customer) ||
            (this.Supplier && this.Customer) ||
            (!this.Supplier && this.Customer)
        ) return true;

        return false;
    }

    async fetch(options?: {}) {
        await Promise.all([
            super.fetch(options)
        ]).catch(e => {
            Events.trigger("navigate", "/relationships");
        });

        if (this.id) {
            this.relationshipUsers = await getAllUsers(+this.id) as any;
        }

        return this;
    }
}


export function getAllUsers(relationshipId: number): Promise<RelationshipUserInfo[]> {
    const url = `/api/inblock/tradetalk/v1/Relationship/${relationshipId}/users`;
    return fetchJson("GET", url);
}

const filterByMultiSelect = (multiSelect: MultiSelectFilter, value): boolean => {
    return !multiSelect || !multiSelect.selected || !multiSelect.selected.length
        || multiSelect.selected.includes(value);
};

export interface PlatformCompany extends AttributesMixin<typeof PlatformCompany> { }
@define
export class PlatformCompany extends Model {
    static endpoint = restfulIO('/api/pts/companies');

    static attributes = {
        companyId: Number,
        name: String,
        phone: String,
        addressLine1: String,
        addressLine2: String,
        city: String,
        stateRegionId: Number,
        countryCode: String,
        postalCode: String,
        legalEntityName: String,
        formationCountryCode: String,
        formationStateRegionId: Number,
    };
}
